import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import Layout from "../components/Layout";
import { Trophy, Medal, BookOpen } from "lucide-react";

const PLACE_TINT = {
  1: "var(--gold)",
  2: "#C9C2B5",
  3: "var(--copper)",
};

const localeMap = { no: "nb-NO", sv: "sv-SE", da: "da-DK", en: "en-GB" };

function fmtDate(iso, lng) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(localeMap[lng] || "nb-NO", { year: "numeric", month: "long" });
  } catch { return iso.slice(0, 7); }
}

function WinnerCard({ row }) {
  const { t, i18n } = useTranslation();
  const isNovelle = row.contest_type === "novelle";
  const typeLabel = t(`hof.type_${row.contest_type}`, { defaultValue: row.contest_type });
  return (
    <article data-testid={`hof-${row.contest_id}`} className="panel-card overflow-hidden flex flex-col">
      <header className="p-5 border-b border-white/8">
        <div className="font-meta text-[var(--gold)] flex items-center gap-2 mb-1">
          <Trophy className="h-3.5 w-3.5" /> {typeLabel}
        </div>
        <h2 className="font-fraunces italic text-2xl text-[var(--cream)] leading-tight">
          {row.contest_title}
        </h2>
        <div className="font-meta text-xs text-[var(--cream-faint)] mt-1">
          {fmtDate(row.closed_at, i18n.language)}
        </div>
      </header>

      <div className="relative aspect-[16/10] bg-[var(--noir-elev)]">
        {row.media?.url ? (
          <img src={row.media.url} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : row.media?.thumbnail_path ? (
          <img src={row.media.thumbnail_path} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : isNovelle ? (
          <div className="absolute inset-0 p-6 flex flex-col">
            <BookOpen className="h-5 w-5 text-[var(--gold)] mb-2" />
            <h3 className="font-fraunces italic text-2xl text-[var(--cream)] mb-3">
              {row.media?.title || t("hof.winning_story")}
            </h3>
            <p className="text-sm text-[var(--cream-soft)] line-clamp-5 leading-relaxed">
              {row.media?.teaser}
            </p>
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Medal className="h-12 w-12 text-[var(--gold)] opacity-30" />
          </div>
        )}
      </div>

      <div className="p-5">
        <div className="flex items-baseline justify-between mb-3">
          <Link to={`/profile/${row.winner?.username}`} className="font-fraunces italic text-xl text-[var(--magenta)] hover:underline">
            {row.winner?.username || t("hof.unknown_winner")}
          </Link>
          <span className="font-meta text-[var(--gold)]">
            +{(row.tokens_awarded || 0).toLocaleString(localeMap[i18n.language] || "nb-NO")} chips
          </span>
        </div>
        {row.all_winners?.length > 1 && (
          <ol className="space-y-1 mb-4">
            {row.all_winners.slice(0, 3).map((w) => (
              <li key={w.entry_id} className="flex items-center gap-3 text-xs font-meta">
                <span style={{ color: PLACE_TINT[w.place] || "var(--cream-faint)" }}>
                  {t("hof.place", { n: w.place })}
                </span>
                <Link to={`/profile/${w.user?.username}`} className="text-[var(--cream)] hover:text-[var(--magenta)] truncate">
                  {w.user?.username || t("hof.unknown_q")}
                </Link>
                <span className="ml-auto text-[var(--cream-faint)]">
                  +{(w.tokens_awarded || 0).toLocaleString(localeMap[i18n.language] || "nb-NO")}
                </span>
              </li>
            ))}
          </ol>
        )}
        <Link to={`/konkurranser/${row.contest_id}`} className="font-meta text-xs text-[var(--magenta)] hover:text-[var(--cream)] inline-block">
          {t("hof.see_contest")}
        </Link>
      </div>
    </article>
  );
}

export default function KonkurranseVinnere() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/contests/winners?limit=60")
      .then((r) => setRows(r.data?.results || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Layout>
      <header className="mb-10">
        <div className="font-meta text-[var(--gold)] mb-3">{t("hof.label")}</div>
        <h1 className="font-fraunces italic text-5xl text-[var(--cream)] leading-tight">
          {t("hof.title")}
        </h1>
        <p className="text-[var(--cream-soft)] text-sm mt-3 max-w-2xl">
          {t("hof.intro")}
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Link to="/konkurranser" data-testid="hof-active-link"
                className="px-4 py-2 border border-[var(--magenta)]/40 text-[var(--magenta)] font-meta hover:bg-[var(--magenta)]/10">
            {t("hof.active_link")}
          </Link>
        </div>
      </header>

      {loading ? (
        <div className="panel p-12 text-center text-[var(--cream-soft)]">{t("hof.loading")}</div>
      ) : rows.length === 0 ? (
        <div className="panel-tight p-12 text-center">
          <Trophy className="h-12 w-12 text-[var(--gold)] mx-auto mb-4 opacity-40" />
          <p className="font-fraunces italic text-2xl text-[var(--cream)] mb-2">
            {t("hof.no_winners")}
          </p>
          <p className="text-[var(--cream-soft)] text-sm">
            {t("hof.no_winners_sub")}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rows.map((r) => <WinnerCard key={r.contest_id} row={r} />)}
        </div>
      )}
    </Layout>
  );
}
